import { useCallback, useEffect, useState } from 'react';
import {
  getAiWorksheetReviewConfig,
  getLatestAiWorksheetReview,
  requestAiWorksheetReview,
} from './api';

const recommendationList = (review) => review?.recommendations?.recommendations || [];

export default function useAiWorksheetReview({ open, uploadId, uploadSource, enabledForRole }) {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [error, setError] = useState('');
  const [configError, setConfigError] = useState('');
  const [summary, setSummary] = useState('');
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!open || !enabledForRole) {
      setFeatureEnabled(false);
      return;
    }
    let cancelled = false;
    setLoadingConfig(true);
    setConfigError('');
    getAiWorksheetReviewConfig()
      .then((res) => {
        if (cancelled) return;
        const enabled = Boolean(res.data?.enabled);
        setFeatureEnabled(enabled);
        if (!enabled) setConfigError('AI worksheet review is disabled in backend settings.');
      })
      .catch(() => {
        if (!cancelled) {
          setFeatureEnabled(false);
          setConfigError('Could not check AI worksheet review availability.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingConfig(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, enabledForRole]);

  useEffect(() => {
    if (!open || !uploadId || !featureEnabled) return;
    let cancelled = false;
    setLoadingLatest(true);
    getLatestAiWorksheetReview(uploadId, uploadSource)
      .then((res) => {
        if (cancelled || res.data?.status !== 'completed') return;
        setSummary(res.data?.recommendations?.summary || '');
        setRecommendations(recommendationList(res.data));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingLatest(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, uploadId, uploadSource, featureEnabled]);

  const runReview = useCallback(async () => {
    if (!uploadId) return null;
    setError('');
    setReviewing(true);
    try {
      const res = await requestAiWorksheetReview(uploadId, uploadSource);
      if (res.data?.status !== 'completed') {
        throw new Error(res.data?.error_message || 'AI review failed.');
      }
      setSummary(res.data?.recommendations?.summary || '');
      const recs = recommendationList(res.data);
      setRecommendations(recs);
      return recs;
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.error_message ||
        err?.message ||
        'AI review failed. Please try again.';
      setError(message);
      return null;
    } finally {
      setReviewing(false);
    }
  }, [uploadId, uploadSource]);

  const updateRecommendation = useCallback((id, changes) => {
    setRecommendations((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  }, []);

  const deleteRecommendation = useCallback((id) => {
    setRecommendations((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const clearRecommendations = useCallback(() => {
    setRecommendations([]);
    setSummary('');
    setError('');
  }, []);

  return {
    featureEnabled,
    configError,
    loadingConfig,
    loadingLatest,
    reviewing,
    error,
    summary,
    recommendations,
    runReview,
    updateRecommendation,
    deleteRecommendation,
    clearRecommendations,
    setError,
  };
}
